import { SubscriptionDeactivationInputModel, UserAccountSubscriptionInterface } from '@spa/api';

/**
 * Parameters needed to map a dialog's selection state to a deactivation request.
 */
export interface SubscriptionDeactivationRequestParams {
    userId: string;
    accountId: string;
    /** UTC calendar date (YYYY-MM-DD), already converted client-side. */
    effectiveDate: string;
    /** IDs of every eligible subscription offered in the dialog. */
    eligibleIds: number[];
    /** IDs the operator currently has checked. */
    selectedIds: number[];
    /** Authoritative active count (from the preview) — required for ALL scope. */
    expectedActiveCount?: number;
    /** Authoritative future count (from the preview) — required for ALL scope. */
    expectedFutureCount?: number;
}

/**
 * Pure, CRM-owned logic for the Market Data subscription deactivation dialog
 * (CRM-5195). Kept free of Angular so it can be unit-tested in isolation.
 *
 * Responsibilities:
 * - determine which subscriptions are eligible for deactivation;
 * - map the dialog selection to the explicit request mode and
 *   build the `@spa/api` `SubscriptionDeactivationInputModel`: the full eligible
 *   list still selected → `ALL_FOR_USER_ACCOUNT` (the server resolves the
 *   complete scope, including rows not visible in CRM); any item unchecked →
 *   `SELECTED` with exactly the remaining checked IDs. The visible list is never
 *   submitted as the complete scope.
 */
export class SubscriptionDeactivationHelper {

    protected constructor() {
    }

    static getEligibleSubscriptions(
        subscriptions: UserAccountSubscriptionInterface[]
    ): UserAccountSubscriptionInterface[] {
        return (subscriptions || []).filter(subscription =>
            !!subscription
            && (subscription.active || subscription.future)
            && !subscription.cancelled
            && !subscription.expired
        );
    }

    static getEligibleIds(subscriptions: UserAccountSubscriptionInterface[]): number[] {
        return SubscriptionDeactivationHelper.unique(
            SubscriptionDeactivationHelper.getEligibleSubscriptions(subscriptions).map(subscription => subscription.id)
        );
    }

    static isFullSelection(eligibleIds: number[], selectedIds: number[]): boolean {
        const eligible = SubscriptionDeactivationHelper.unique(eligibleIds);
        const selectedEligible = SubscriptionDeactivationHelper.unique(selectedIds)
            .filter(id => eligible.indexOf(id) !== -1);

        return eligible.length > 0 && selectedEligible.length === eligible.length;
    }

    static buildDeactivationRequest(
        params: SubscriptionDeactivationRequestParams
    ): SubscriptionDeactivationInputModel | null {
        const eligible = SubscriptionDeactivationHelper.unique(params.eligibleIds);
        const selectedEligible = SubscriptionDeactivationHelper.unique(params.selectedIds)
            .filter(id => eligible.indexOf(id) !== -1);

        if (selectedEligible.length === 0) {
            return null;
        }

        if (SubscriptionDeactivationHelper.isFullSelection(eligible, selectedEligible)) {
            return SubscriptionDeactivationInputModel.forUserAccount(
                params.userId,
                params.accountId,
                params.effectiveDate,
                params.expectedActiveCount,
                params.expectedFutureCount
            );
        }

        return SubscriptionDeactivationInputModel.forSelected(
            params.userId,
            params.accountId,
            selectedEligible,
            params.effectiveDate
        );
    }

    private static unique(ids: number[]): number[] {
        return (ids || []).filter((id, index, all) => all.indexOf(id) === index);
    }
}
